// Tables.tsx mein yeh complete file hai
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import MobileTailwind from "./MobileTailwind";
import TableSeatingIcon from "./components/TableSeatingIcon";
import SelectAreaPopup from "./components/SelectAreaPopup";
import StopTimerPopup from "./components/StopTimerPopup";
import BottomNav from "./components/BottomNav";
import HomeHeader from "./components/HomeHeader";
import { toast } from "react-toastify";
import { getEncryptedSessionData, setEncryptedSessionData } from "../Services/SessionStorageService";
import QrOrderRealtimeService, { QrOrderPayload } from "../Services/QrOrderRealtimeService";
import { LoungeAttributes } from "../components/Common/BackendAttributes/BackendAttributes";
import WebService from "../Services/WebService";
import MobileLoader from "../components/MobileLoader/MobileLoader";
import SubHeader from "./components/SubHeader";
import qrOrderRealtimeService from "../Services/QrOrderRealtimeService";
import { io } from "socket.io-client";
import { useCart } from "./CartContext";

// ─── POS Socket ───────────────────────────────────────────────────────────────

const getPosSocket = () => {
  const envUrl = process.env.REACT_APP_POS_SOCKET_URL;
  const stored = localStorage.getItem("pos_socket_url");
  const host = window.location.hostname;
  let url = envUrl || stored || "http://localhost:3007";
  if (!envUrl && (!stored || stored === "http://localhost:3007") && host !== "localhost" && host !== "127.0.0.1") {
    url = `http://${host}:3007`;
  }
  return io(url, {
    autoConnect: true,
    transports: ["websocket", "polling"],
    reconnectionDelay: 5000,
    timeout: 5000
  });
};
let _posSocket: ReturnType<typeof io> | null = null;
export const posSocket = () => {
  if (!_posSocket) _posSocket = getPosSocket();
  return _posSocket;
};

// ─── Types ────────────────────────────────────────────────────────────────────

export interface PosOrderData {
  menus: any[];
  timestamp: string;
  type: "POS_ORDER" | "QR_ORDER";
  loungeId?: string | number;
  tokenNumber?: string | number;
  isNew: boolean;
  serverTime?: string;
  fetchedAt?: number;
}

export const getPosOrderCacheKey = (tenantId: string) => `pos_orders_by_table_${tenantId}`;
export const normalizeTableNo = (value: any) => String(value || "").trim();

const parseLoungeTables = (tables: any): any[] => {
  if (Array.isArray(tables)) return tables;
  try {
    const parsed = JSON.parse(tables);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const getTableNumber = (table: any) => {
  if (typeof table === "object" && table !== null) {
    return normalizeTableNo(table.table_no || table.tableNo || table.id);
  }
  return normalizeTableNo(table);
};

// ─── Timer Hook ───────────────────────────────────────────────────────────────

const useTableTimers = (posOrdersByTable: Record<string, PosOrderData>) => {
  const [timers, setTimers] = useState<Record<string, string>>({});

  useEffect(() => {
    const tick = () => {
      const now = Date.now();
      const updated: Record<string, string> = {};
      Object.entries(posOrdersByTable).forEach(([tableNo, data]) => {
        if (!data.timestamp) return;
        let diffMs = now - new Date(data.timestamp).getTime();
        if (data.serverTime && data.fetchedAt) {
          const serverTimeMs = new Date(data.serverTime).getTime();
          const timestampMs = new Date(data.timestamp).getTime();
          if (!isNaN(serverTimeMs) && !isNaN(timestampMs)) {
            const initialServerElapsed = serverTimeMs - timestampMs;
            const timeSinceFetch = now - data.fetchedAt;
            diffMs = initialServerElapsed + timeSinceFetch;
          }
        }
        const diff = Math.floor(diffMs / 1000);
        if (diff < 0) return;
        const h = Math.floor(diff / 3600);
        const m = Math.floor((diff % 3600) / 60);
        const s = diff % 60;
        updated[tableNo] =
          h > 0
            ? `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
            : `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
      });
      setTimers(updated);
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [posOrdersByTable]);

  return timers;
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const ClockIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="9" stroke="#F15A29" strokeWidth="2" />
    <path d="M12 7v5l3 2" stroke="#F15A29" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

interface TableCardProps {
  tableNo: string;
  hasOrder: boolean;
  isNew: boolean;
  timer?: string;
}

const TableCard = ({ tableNo, hasOrder, isNew, timer }: TableCardProps) => {
  if (hasOrder) {
    return (
      <div
        className={`w-full aspect-square rounded-[6px] flex flex-col items-center justify-center p-2 transition-all duration-200 cursor-pointer relative ${isNew ? "tv-pulse-anim" : ""}`}
        style={{
          backgroundColor: '#FFF5F2',
          border: '1px solid #F15A29'
        }}
      >
        {/* Table Number in dead center */}
        <span className="font-sansation font-bold text-[#F15A29] text-[18px] leading-none text-center truncate max-w-full px-1">
          {tableNo}
        </span>

        {/* Timer & Status */}
        <div className="absolute bottom-2 left-0 right-0 flex flex-col items-center gap-0.5 w-full">
          {timer ? (
            <div className="flex items-center gap-1 justify-center">
              <ClockIcon />
              <span className="font-sansation font-bold text-[#F15A29] text-[10px] tracking-[0.5px]">
                {timer}
              </span>
            </div>
          ) : null}
          <span className="text-[9px] font-bold text-[#F15A29] uppercase tracking-wider leading-none">
            Occupied
          </span>
        </div>
      </div>
    );
  }

  return (
    <div
      className="w-full aspect-square rounded-[6px] flex flex-col items-center justify-center p-2 transition-all duration-200 cursor-pointer relative"
      style={{
        backgroundColor: '#F2F2F2',
        border: '1.5px dashed #888888'
      }}
    >
      {/* Table Number in dead center */}
      <span className="font-sansation font-bold text-[#475569] text-[18px] leading-none text-center truncate max-w-full px-1">
        {tableNo}
      </span>

      {/* Status */}
      <span className="absolute bottom-2 left-0 right-0 text-center text-[9px] font-bold text-[#10B981] uppercase tracking-wider leading-none">
        Available
      </span>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

interface TablesProps {
  onSelectTable?: (no: string) => void;
}

const Tables = ({ onSelectTable }: TablesProps) => {
  const navigate = useNavigate();
  const cart = useCart();
  const filterBtnRef = useRef<HTMLButtonElement>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [selectedAreas, setSelectedAreas] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  const [stopTimerTable, setStopTimerTable] = useState<{ no: string; timer?: string; label: string } | null>(null);
  const [isLoading, setLoading] = useState(false);
  const [loungeList, setLoungeList] = useState<LoungeAttributes[]>([]);
  const originalLoungeList = useRef<LoungeAttributes[]>([]);
  const tenantId = localStorage.getItem("tenantId") || "default";
  const loungeCacheKey = `table_view_lounge_list_${tenantId}`;
  const qrOrderCacheKey = `qr_table_orders_${tenantId}`;
  const posOrderCacheKey = getPosOrderCacheKey(tenantId);
  const [qrOrdersByTable, setQrOrdersByTable] = useState<Record<string, QrOrderPayload>>({});
  const [posOrdersByTable, setPosOrdersByTable] = useState<Record<string, PosOrderData>>({});
  const timers = useTableTimers(posOrdersByTable);

  // ── Initial load ───────────────────────────────────────────────────────────

  const fetchActiveTables = () => {
    const tenantId = localStorage.getItem("tenantId") || "default";
    WebService.getAPI<any>({
      action: "/waiter/get-active-tables",
      body: { tenantId },
      isShowError: false
    })
      .then((res: any) => {
        const fetchedOrders: Record<string, PosOrderData> = {};
        if (res?.tableOrders) {
          Object.entries(res.tableOrders).forEach(([tableNo, data]: [string, any]) => {
            fetchedOrders[tableNo] = {
              menus: data.menus,
              timestamp: data.timestamp || new Date().toISOString(),
              type: "POS_ORDER",
              loungeId: data.lounge_id,
              tokenNumber: data.token_number,
              isNew: false,
              serverTime: data.serverTime,
              fetchedAt: Date.now(),
            };
          });
        }
        setPosOrdersByTable(fetchedOrders);
        setEncryptedSessionData(posOrderCacheKey, fetchedOrders);
      })
      .catch((err) => {
        console.error("fetchActiveTables error:", err);
      });
  };

  useEffect(() => {
    getLoungeList();
    setQrOrdersByTable(getEncryptedSessionData<Record<string, QrOrderPayload>>(qrOrderCacheKey) || {});
    const savedPosOrders = getEncryptedSessionData<Record<string, PosOrderData>>(posOrderCacheKey) || {};
    const restored = Object.fromEntries(
      Object.entries(savedPosOrders).map(([k, v]) => [k, { ...(v as PosOrderData), isNew: false }])
    );
    setPosOrdersByTable(restored);
    fetchActiveTables();
  }, []);

  // ── POS order cache updater ────────────────────────────────────────────────

  const updatePosOrders = (updater: (prev: Record<string, PosOrderData>) => Record<string, PosOrderData>) => {
    setPosOrdersByTable((prev) => {
      const next = updater(prev);
      setEncryptedSessionData(posOrderCacheKey, next);
      return next;
    });
  };

  // ── POS Socket listener — dusre system se aane wale orders ke liye ────────

  useEffect(() => {
    const s = posSocket();
    const handleWaiterOrder = (payload: any) => {
      // Tenant ID validation
      const eventTenantId = String(payload?.tenantId || payload?.tenant_id || "");
      const localTenantId = localStorage.getItem("tenantId") || "default";
      if (eventTenantId && eventTenantId !== localTenantId) {
        console.log("POS Socket Tenant ID mismatch, ignoring waiterOrderCreated:", eventTenantId, "expected:", localTenantId);
        return;
      }

      const tableNo = normalizeTableNo(payload?.table_no || payload?.tableNo);
      if (!tableNo) return;
      const action = payload?.action || "";
      if (action === "CLEAR_TABLE") {
        setQrOrdersByTable((prev) => {
          const next = { ...prev };
          delete next[tableNo];
          setEncryptedSessionData(qrOrderCacheKey, next);
          return next;
        });
        updatePosOrders((prev) => {
          const next = { ...prev };
          delete next[tableNo];
          return next;
        });
        return;
      }

      // ── Ab yeh API kaam karegi ──
      const envUrl = process.env.REACT_APP_POS_SOCKET_URL;
      const stored = localStorage.getItem("pos_socket_url");
      const host = window.location.hostname;
      let posUrl = envUrl || stored || "http://localhost:3007";
      if (!envUrl && (!stored || stored === "http://localhost:3007") && host !== "localhost" && host !== "127.0.0.1") {
        posUrl = `http://${host}:3007`;
      }

      setTimeout(() => {
        fetch(`${posUrl}/get-order-by-table/${tableNo}`)
          .then((r) => r.json())
          .then((res: any) => {
            const menus: any[] = res?.menus || [];
            if (menus.length === 0) return; // Keep existing menus to prevent premature overwrite

            updatePosOrders((prev) => ({
              ...prev,
              [tableNo]: {
                menus,
                timestamp: res?.timestamp || payload?.timestamp || prev[tableNo]?.timestamp || new Date().toISOString(),
                type: "POS_ORDER",
                loungeId: payload?.lounge_id || prev[tableNo]?.loungeId,
                tokenNumber: payload?.token_number || res?.token_number || prev[tableNo]?.tokenNumber || "",
                isNew: true,
                serverTime: res?.serverTime || payload?.serverTime || prev[tableNo]?.serverTime,
                fetchedAt: Date.now(),
              },
            }));

            setTimeout(() => {
              updatePosOrders((prev) => {
                if (!prev[tableNo]) return prev;
                return { ...prev, [tableNo]: { ...prev[tableNo], isNew: false } };
              });
            }, 5000);
          })
          .catch(() => {
            // API fail — sirf occupied mark karo
            updatePosOrders((prev) => ({
              ...prev,
              [tableNo]: {
                menus: prev[tableNo]?.menus || [{ id: "ph", name: "Order placed", quantity: 1, gross_price: 0 }],
                timestamp: prev[tableNo]?.timestamp || new Date().toISOString(),
                type: "POS_ORDER",
                loungeId: payload?.lounge_id || prev[tableNo]?.loungeId,
                tokenNumber: payload?.token_number || prev[tableNo]?.tokenNumber || "",
                isNew: true,
                serverTime: payload?.serverTime || prev[tableNo]?.serverTime,
                fetchedAt: Date.now(),
              },
            }));
          });
      }, 1500);
    };

    const handleBillCleared = (payload: any) => {
      // Tenant ID validation
      const eventTenantId = String(payload?.tenantId || payload?.tenant_id || "");
      const localTenantId = localStorage.getItem("tenantId") || "default";
      if (eventTenantId && eventTenantId !== localTenantId) {
        console.log("POS Socket Tenant ID mismatch, ignoring bill cleared event:", eventTenantId, "expected:", localTenantId);
        return;
      }

      const tableNo = normalizeTableNo(payload?.table_no || payload?.tableNo);
      if (!tableNo) return;
      setQrOrdersByTable((prev) => {
        const next = { ...prev };
        delete next[tableNo];
        setEncryptedSessionData(qrOrderCacheKey, next);
        return next;
      });
      updatePosOrders((prev) => {
        const next = { ...prev };
        delete next[tableNo];
        return next;
      });
    };

    s.on("waiterOrderCreated", handleWaiterOrder);
    s.on("orderBillCleared", handleBillCleared);
    s.on("tableBillCleared", handleBillCleared);
    s.on("CLEAR_TABLE", handleBillCleared);

    if (!s.connected) s.connect();

    return () => {
      s.off("waiterOrderCreated", handleWaiterOrder);
      s.off("orderBillCleared", handleBillCleared);
      s.off("tableBillCleared", handleBillCleared);
      s.off("CLEAR_TABLE", handleBillCleared);
    };
  }, [posOrderCacheKey, qrOrderCacheKey]);

  // ── QrOrderRealtimeService listener ───────────────────────────────────────

  useEffect(() => {
    const unsubscribe = qrOrderRealtimeService.subscribe((order: QrOrderPayload) => {
      // ── Tenant ID check ──
      const orderTenantId = String(order.tenantId || order.tenant_id || "").trim().toLowerCase();
      const localTenantId = String(localStorage.getItem("tenantId") || "default").trim().toLowerCase();
      if (orderTenantId && orderTenantId !== localTenantId) {
        console.log("Tenant ID mismatch, ignoring order event:", orderTenantId, "expected:", localTenantId);
        return;
      }

      const incomingType = order.type || "";

      if (incomingType === "ACTIVE_TABLES") {
        console.log("=== ACTIVE_TABLES event received ===", order.tableOrders);
        const fetchedOrders: Record<string, PosOrderData> = {};
        if (order.tableOrders) {
          Object.entries(order.tableOrders).forEach(([tNo, data]: [string, any]) => {
            fetchedOrders[tNo] = {
              menus: data.menus,
              timestamp: data.timestamp || new Date().toISOString(),
              type: "POS_ORDER",
              loungeId: data.lounge_id,
              tokenNumber: data.token_number,
              isNew: false,
              serverTime: data.serverTime,
              fetchedAt: Date.now(),
            };
          });
        }
        setPosOrdersByTable(fetchedOrders);
        setEncryptedSessionData(posOrderCacheKey, fetchedOrders);
        return;
      }

      const tableNo = normalizeTableNo(order.tableNo || order.table_no);
      if (!tableNo) return;
      const action = order.action || "ADD";

      if (action === "CLEAR_TABLE") {
        setQrOrdersByTable((prev) => {
          const next = { ...prev };
          delete next[tableNo];
          setEncryptedSessionData(qrOrderCacheKey, next);
          return next;
        });
        updatePosOrders((prev) => {
          const next = { ...prev };
          delete next[tableNo];
          setEncryptedSessionData(posOrderCacheKey, next);
          return next;
        });
        return;
      }

      if (incomingType === "POS_ORDER") {
        const freshMenus = order.menus || [];
        updatePosOrders((prev) => ({
          ...prev,
          [tableNo]: {
            menus: freshMenus,
            timestamp: order.timestamp || new Date().toISOString(),
            type: "POS_ORDER",
            loungeId: order.loungeId || order.lounge_id,
            tokenNumber: order.tokenNumber || order.token_number,
            isNew: true,
            serverTime: order.serverTime || prev[tableNo]?.serverTime,
            fetchedAt: Date.now(),
          },
        }));
        setTimeout(() => {
          updatePosOrders((prev) => {
            if (!prev[tableNo]) return prev;
            return { ...prev, [tableNo]: { ...prev[tableNo], isNew: false } };
          });
        }, 5000);
        return;
      }

      if (
        incomingType === "QR_ORDER" ||
        (!incomingType && action !== "FULL_REPLACE" && action !== "CLEAR_TABLE" && incomingType !== "POS_ORDER")
      ) {
        setQrOrdersByTable((prev) => {
          const next = { ...prev, [tableNo]: order };
          setEncryptedSessionData(qrOrderCacheKey, next);
          return next;
        });
        updatePosOrders((prev) => ({
          ...prev,
          [tableNo]: {
            menus: order.menus || [],
            timestamp: order.timestamp || new Date().toISOString(),
            type: "QR_ORDER",
            loungeId: order.loungeId || order.lounge_id,
            tokenNumber: order.tokenNumber || order.token_number,
            isNew: true,
            serverTime: order.serverTime || prev[tableNo]?.serverTime,
            fetchedAt: Date.now(),
          },
        }));
        setTimeout(() => {
          updatePosOrders((prev) => {
            if (!prev[tableNo]) return prev;
            return { ...prev, [tableNo]: { ...prev[tableNo], isNew: false } };
          });
        }, 5000);
        return;
      }

      updatePosOrders((prev) => {
        const existing = prev[tableNo];
        if (!existing) return prev;
        return { ...prev, [tableNo]: { ...existing, isNew: false } };
      });
    });
    return unsubscribe;
  }, [qrOrderCacheKey, posOrderCacheKey]);

  // ── API calls ──────────────────────────────────────────────────────────────

  // ── SWR (Stale-While-Revalidate) lounge list load ──
  const getLoungeList = () => {
    const cached = getEncryptedSessionData<LoungeAttributes[]>(loungeCacheKey);
    if (cached) {
      setLoungeList(cached);
      originalLoungeList.current = cached;
    } else {
      setLoading(true);
    }
    WebService.getAPI({ action: "/get-all-lounge-list", body: {} })
      .then((response: any) => {
        const lounges = response?.lounges || [];
        setLoungeList(lounges);
        originalLoungeList.current = lounges;
        setEncryptedSessionData(loungeCacheKey, lounges);
      })
      .catch((err) => console.error("Lounge list swr fetch error:", err))
      .finally(() => setLoading(false));
  };

  // ── Search filter ──────────────────────────────────────────────────────────

  const filterTableNo = (value: string) => {
    setSearch(value);
    if (value.trim() === "") {
      setLoungeList(originalLoungeList.current);
      return;
    }
    let found = false;
    const filtered = originalLoungeList.current
      .map((lounge) => {
        const tables = parseLoungeTables(lounge.lounge_tables);
        const ft = tables.filter((t: any) => {
          const no = typeof t === "string" ? t : t.table_no || t.tableNo || "";
          return no.toLowerCase().includes(value.toLowerCase());
        });
        if (ft.length > 0) found = true;
        return { ...lounge, lounge_tables: JSON.stringify(ft) };
      })
      .filter((l) => parseLoungeTables(l.lounge_tables).length > 0);

    if (!found) {
      toast.error("Table not found");
      setLoungeList(originalLoungeList.current);
    } else {
      setLoungeList(filtered);
    }
  };

  // ── Navigation ─────────────────────────────────────────────────────────────

  const openTableOrder = (tableNo: string, loungeId: number | string) => {
    const posData = posOrdersByTable[tableNo];
    const qrOrder = qrOrdersByTable[tableNo];
    const params = new URLSearchParams({ loungeId: String(loungeId) });

    const targetOrder = posData?.type === "QR_ORDER" ? posData : (qrOrder || null);

    if (targetOrder && targetOrder.menus && targetOrder.menus.length > 0) {
      const cartLines = targetOrder.menus.map((item: any, idx: number) => {
        const dishId = String(item.menu_id || item.id || item.item_id || `item_${idx}`);
        const dishCode = Number(item.code || item.custom_code || 0);
        const dish = {
          id: dishId,
          name: item.name || item.item_name || item.menu_name || "Item",
          code: dishCode,
          price: Number(item.gross_price ?? item.price ?? item.unit_price ?? 0),
          type: (item.type || (String(item.food_type || "").toLowerCase().includes("non") ? "nonveg" : "veg")) as "veg" | "nonveg",
        };
        return {
          key: `${dish.id}-${dish.code}`,
          dish: dish,
          quantity: Number(item.quantity || 1),
          note: item.special_note || item.note || "",
        };
      });

      cart.setCartLines(tableNo, cartLines);
      cart.setTable(tableNo);
      onSelectTable?.(tableNo);
      navigate(`/mobile/menu/${tableNo}?${params.toString()}`);
    } else {
      onSelectTable?.(tableNo);
      navigate(`/mobile/menu/${tableNo}?${params.toString()}`);
    }
  };

  const handleTableClick = (tableNo: string, loungeId: number | string) => {
    const posData = posOrdersByTable[tableNo];
    const isAlreadyPlaced = Boolean(posData && posData.type !== "QR_ORDER");
    if (isAlreadyPlaced) {
      navigate(`/mobile/order-details/${tableNo}`);
      return;
    }
    openTableOrder(tableNo, loungeId);
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      <style>{`
        @keyframes tv-pulse {
          0%   { box-shadow: 0 0 0 0 rgba(241,90,41,0.8); }
          70%  { box-shadow: 0 0 0 10px rgba(241,90,41,0); }
          100% { box-shadow: 0 0 0 0 rgba(241,90,41,0); }
        }
        .tv-pulse-anim { animation: tv-pulse 1s ease-out infinite; border-radius: 6px; }
      `}</style>
      <MobileTailwind />
      <MobileLoader show={isLoading} />
      <div id="mobile-root" className="h-screen w-full bg-black flex justify-center overflow-hidden">
        <div className="relative w-full h-screen overflow-y-auto overflow-x-hidden">
          <div className="absolute inset-0 z-0">
            <img src="/mobile-assets/login-bg.jpg" alt="" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-black/80" />
          </div>

          <div className="relative z-10">
            <SubHeader title="Tables" showBack={false} />
            <HomeHeader
              searchValue={search}
              onSearchChange={filterTableNo}
              filterOpen={filterOpen}
              onToggleFilter={() => setFilterOpen((o) => !o)}
              filterBtnRef={filterBtnRef}
              searchPlaceholder="Search table number"
            />
            <SelectAreaPopup
              open={filterOpen}
              onClose={() => setFilterOpen(false)}
              selected={selectedAreas}
              onChange={setSelectedAreas}
              anchorRef={filterBtnRef}
            />

            <div className="px-3 md:px-6 pt-3 pb-[120px]">
              {loungeList.map((lounge: any) => {
                const tables = parseLoungeTables(lounge.lounge_tables);
                if (tables.length === 0) return null;
                return (
                  <div key={lounge.id} className="mb-4">
                    <div className="text-white/60 text-[11px] font-semibold uppercase tracking-widest mb-2 px-1">
                      {lounge.name}
                    </div>
                    <div className="grid grid-cols-3 min-[400px]:grid-cols-4 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-7 xl:grid-cols-8 gap-3">
                      {tables.map((table: any, index: number) => {
                        const tableNo = getTableNumber(table);
                        const posData = posOrdersByTable[tableNo];
                        const hasOrder = Boolean(posData);
                        const isNew = Boolean(posData?.isNew);
                        const timer = timers[tableNo];
                        return (
                          <button
                            key={`${tableNo}-${index}`}
                            type="button"
                            onClick={() => handleTableClick(tableNo, lounge.id)}
                            className="w-full text-left p-0 bg-transparent border-0"
                          >
                            <TableCard
                              tableNo={tableNo}
                              hasOrder={hasOrder}
                              isNew={isNew}
                              timer={timer}
                            />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            <BottomNav active="home" />
            <StopTimerPopup
              open={!!stopTimerTable}
              onClose={() => setStopTimerTable(null)}
              tableNo={stopTimerTable?.no ?? ""}
              timer={stopTimerTable?.timer}
              occupiedCount={stopTimerTable ? parseInt(stopTimerTable.label, 10) || undefined : undefined}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default Tables;